import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { TrendingUp, Clock, Target, ExternalLink, ShieldCheck, FileCheck, FileSearch } from 'lucide-react';

const CaseStudies = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Dark Navy */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#060D1B] via-[#0A1628] to-[#0d1f3c]">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center" data-testid="case-studies-header">
            <h1 className="text-h1 text-white mb-6">Intelligence in Production</h1>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              Active pilots and early deployments. Real systems, real data, real results — in progress.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section-padding px-6 bg-[#EFF6FF]">
        <div className="max-w-[1200px] mx-auto space-y-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-h3 text-[#0A1628] mb-4">Selected Pilots and Early Deployments</h2>
            <p className="text-body-lg text-[#6B7280] leading-relaxed">
              A snapshot of how Cognifin is being used in live banking and risk workflows, with measurable outcomes already in view.
            </p>
          </div>
          
          {/* Case Study 1 - Tier-1 Retail Bank */}
          <Card className="bg-white border-[#D1D5DB] rounded-xl overflow-hidden" data-testid="case-study-1">
            <CardHeader className="border-b border-[#D1D5DB]">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="px-4 py-2 bg-[#0E7490]/10 rounded-full text-[#0E7490] text-sm font-semibold">
                    Retail Banking
                  </div>
                </div>
              </div>
              <CardTitle className="text-[#0A1628] text-3xl mt-4">Tier-1 Retail Bank</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-12">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-3">Challenge</h3>
                    <p className="text-[#6B7280]">
                      Manual credit assessment process taking 48+ hours with limited behavioural insights from traditional credit bureaus.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-3">Solution</h3>
                    <p className="text-[#6B7280]">
                      Deployed Cognifin Financial Data Intelligence and Underwriting Co-worker in a focused 4-week pilot.
                    </p>
                  </div>
                  
                  <div className="bg-[#EFF6FF] border border-[#D1D5DB] rounded-xl p-6">
                    <p className="text-[#374151] italic">
                      "The AI co-worker handles the document-heavy prep work. Our team now spends time on credit judgment, not data gathering."
                    </p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-6">Key Results <span className="text-sm font-normal text-[#6B7280]">(early indicators)</span></h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-[#1A56DB]/10 rounded-lg">
                        <Clock className="text-[#1A56DB]" size={24} />
                      </div>
                      <div>
                        <div className="text-4xl font-bold text-[#1A56DB] mb-1">85%</div>
                        <div className="text-[#6B7280]">Reduction in TAT <span className="text-[#0E7490] text-sm font-medium">(targeted)</span></div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-[#1A56DB]/10 rounded-lg">
                        <Target className="text-[#1A56DB]" size={24} />
                      </div>
                      <div>
                        <div className="text-4xl font-bold text-[#1A56DB] mb-1">35%</div>
                        <div className="text-[#6B7280]">Improvement in default prediction accuracy <span className="text-[#0E7490] text-sm font-medium">(in testing)</span></div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-[#1A56DB]/10 rounded-lg">
                        <FileSearch className="text-[#1A56DB]" size={24} />
                      </div>
                      <div>
                        <div className="text-4xl font-bold text-[#1A56DB] mb-1">100%</div>
                        <div className="text-[#6B7280]">Loan applications with full audit trail</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Case Study 2 - Leading NBFC */}
          <Card className="bg-white border-[#D1D5DB] rounded-xl overflow-hidden" data-testid="case-study-2">
            <CardHeader className="border-b border-[#D1D5DB]">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="px-4 py-2 bg-[#4F46E5]/10 rounded-full text-[#4F46E5] text-sm font-semibold">
                    NBFC & Business Lending
                  </div>
                </div>
              </div>
              <CardTitle className="text-[#0A1628] text-3xl mt-4">Leading NBFC</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-12">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-3">Challenge</h3>
                    <p className="text-[#6B7280]">
                      Complex GST data analysis and bank statement reconciliation requiring specialized expertise and significant time.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-3">Solution</h3>
                    <p className="text-[#6B7280]">
                      Deployed Agentic Co-workers for automated triangulation and CAM generation with Vision AI for collateral verification.
                    </p>
                  </div>
                  
                  <div className="bg-[#EFF6FF] border border-[#D1D5DB] rounded-xl p-6">
                    <p className="text-[#374151] italic">
                      "The AI agents handle complexity that previously required senior analysts, freeing our team for strategic decisions."
                    </p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-6">Key Results <span className="text-sm font-normal text-[#6B7280]">(early indicators)</span></h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-[#1A56DB]/10 rounded-lg">
                        <Clock className="text-[#1A56DB]" size={24} />
                      </div>
                      <div>
                        <div className="text-4xl font-bold text-[#1A56DB] mb-1">&lt;15min</div>
                        <div className="text-[#6B7280]">Average CAM generation time</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-[#1A56DB]/10 rounded-lg">
                        <TrendingUp className="text-[#1A56DB]" size={24} />
                      </div>
                      <div>
                        <div className="text-4xl font-bold text-[#1A56DB] mb-1">3x</div>
                        <div className="text-[#6B7280]">Improvement in underwriter throughput <span className="text-[#4F46E5] text-sm font-medium">(in-progress)</span></div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-[#1A56DB]/10 rounded-lg">
                        <ShieldCheck className="text-[#1A56DB]" size={24} />
                      </div>
                      <div>
                        <div className="text-4xl font-bold text-[#1A56DB] mb-1">100%</div>
                        <div className="text-[#6B7280]">Policy compliance verification <span className="text-[#4F46E5] text-sm font-medium">(targeting)</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Case Study 3 - Aureon: Regional Bank */}
          <Card className="bg-white border-[#D1D5DB] rounded-xl overflow-hidden" data-testid="case-study-3">
            <CardHeader className="border-b border-[#D1D5DB]">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="px-4 py-2 bg-[#B45309]/10 rounded-full text-[#B45309] text-sm font-semibold">
                    Model Risk Management
                  </div>
                </div>
              </div>
              <CardTitle className="text-[#0A1628] text-3xl mt-4">Regional Bank — Model Governance</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-12">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-3">Challenge</h3>
                    <p className="text-[#6B7280]">
                      Manual MRM process relying on spreadsheets, disconnected validation documentation, and examiner pack preparation taking weeks per model review cycle.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-3">Solution</h3>
                    <p className="text-[#6B7280]">
                      Deployed Aureon (a Cognifin Platform) for end-to-end model lifecycle governance. AI co-workers draft validation documentation, surface exceptions, and auto-generate examiner packs.
                    </p>
                  </div>
                  
                  <div className="bg-[#EFF6FF] border border-[#D1D5DB] rounded-xl p-6">
                    <p className="text-[#374151] italic">
                      "What used to take our risk team weeks to compile now takes hours. The packs come out complete and consistent every time."
                    </p>
                  </div>

                  <a 
                    href="https://aureon-mrm.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#B45309] font-semibold hover:underline"
                  >
                    Learn more about Aureon
                    <ExternalLink size={16} />
                  </a>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-6">Key Results <span className="text-sm font-normal text-[#6B7280]">(early indicators)</span></h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-[#B45309]/10 rounded-lg">
                        <Clock className="text-[#B45309]" size={24} />
                      </div>
                      <div>
                        <div className="text-4xl font-bold text-[#B45309] mb-1">90%+</div>
                        <div className="text-[#6B7280]">Reduction in examiner pack preparation time <span className="text-[#0E7490] text-sm font-medium">(targeted)</span></div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-[#B45309]/10 rounded-lg">
                        <FileCheck className="text-[#B45309]" size={24} />
                      </div>
                      <div>
                        <div className="text-4xl font-bold text-[#B45309] mb-1">100%</div>
                        <div className="text-[#6B7280]">Model lifecycle documentation coverage</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-[#B45309]/10 rounded-lg">
                        <FileSearch className="text-[#B45309]" size={24} />
                      </div>
                      <div>
                        <div className="text-4xl font-bold text-[#B45309] mb-1">Full</div>
                        <div className="text-[#6B7280]">Audit trail across validation, monitoring, and governance</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding px-6 bg-gradient-to-r from-[#0A1628] to-[#1A56DB]" data-testid="case-studies-cta">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-white mb-6">
            Ready to Start Your Pilot?
          </h2>
          <p className="text-body-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Join financial institutions exploring AI-native intelligence for credit operations
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-white hover:bg-white/90 text-[#0A1628] font-bold text-lg px-10 h-14 btn-hover-lift shadow-xl"
            data-testid="schedule-demo-btn"
          >
            <Link to="/contact">Schedule a Demo</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
